import React from 'react';
import {
  Button,
  CloseDefaultIcon,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  Text,
  View,
  VStack,
  type AnchorRef,
} from '@packages/systems/spring';
import {selectIsCodeComponentCanvas} from '@packages/systems/component-canvas/universal';
import {
  selectComponentCanvasComponentId,
  switchToComponentCanvasStarted,
} from '@packages/systems/component-canvas/designer';
import {getCodeIslandContentElements} from '@packages/systems/code-libraries/inner-node-utils';
import {getActiveDocument} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {useFirstTimeUse} from '@packages/systems/designer/apps/client';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {CodeIslandCustomElementStore} from '@packages/systems/plugins/Code';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';
import {Component} from '@packages/systems/wfdl';
import {
  getLocalCodeComponents,
  isCodeComponentWithSourceComponent,
} from '@packages/utilities/platform';
import {createSelector} from 'reselect';
import {emotionStyled} from '@packages/utilities/styled';
import {
  selectShowCodeComponentCreator,
  selectShowVisualEditingOnboarding,
  selectVisualEditingOnboardingStep,
  useCodeComponentCreated,
  useCodeComponentDispatch,
  useCodeComponentSelector,
} from './flux/CodeLibrariesSlice';
import {
  visualEditingOnboardingAdvanced,
  visualEditingOnboardingDismissed,
  visualEditingOnboardingStarted,
} from './flux/actionCreators';
import {useAssistantChatAnchor} from './useAssistantChatAnchor';
import {VISUAL_EDITING_ONBOARDING_VIDEOS} from './visualEditingOnboardingVideos';

const LEGACY_VISUAL_EDITING_ONBOARDING_NOTE_ID = 'VISUAL_EDITING_ONBOARDING';
const VISUAL_EDITING_ONBOARDING_POPOVER_WIDTH = 281;
export const AICC_USED_NOTE_ID = 'AI_CODE_COMPONENT_USED';
export const VCC_ONBOARDING_SELECTION_SOURCE = 'visual editing onboarding';
export const VISUAL_EDITING_ONBOARDING_NOTE_ID =
  'VISUAL_EDITING_ONBOARDING_POPOVER';

// Only local, source-backed components open on the component canvas.
export const selectEditableCodeComponentIds = createSelector(
  (state: DesignerStores) => state.DesignerStore.components,
  (components): ReadonlySet<string> => {
    const componentIds = new Set<string>();

    for (const [componentId, component] of Object.entries(
      getLocalCodeComponents(components)
    )) {
      if (
        Component.getOrigin(component) === undefined &&
        isCodeComponentWithSourceComponent(component)
      ) {
        componentIds.add(componentId);
      }
    }

    return componentIds;
  }
);

export const selectHasCodeComponents = createSelector(
  (state: DesignerStores) => state.DesignerStore.components,
  (components): boolean =>
    Object.keys(getLocalCodeComponents(components)).length > 0
);

export function shouldStartStandaloneVisualEditingOnboarding({
  canEditCodeComponents,
  componentCanvasComponentId,
  editableCodeComponentIds,
  isOnboardingFirstTimeUse,
  isVisualEditingEnabled,
  showVisualEditingOnboarding,
}: {
  canEditCodeComponents: boolean;
  componentCanvasComponentId: string | undefined;
  editableCodeComponentIds: ReadonlySet<string>;
  isOnboardingFirstTimeUse: boolean;
  isVisualEditingEnabled: boolean;
  showVisualEditingOnboarding: boolean;
}): boolean {
  // The blank component is inserted before the creator opens, so one ID here
  // means the user had zero code components before starting this creation.
  return (
    canEditCodeComponents &&
    isVisualEditingEnabled &&
    isOnboardingFirstTimeUse &&
    !showVisualEditingOnboarding &&
    componentCanvasComponentId !== undefined &&
    editableCodeComponentIds.size === 1 &&
    editableCodeComponentIds.has(componentCanvasComponentId)
  );
}

const canEditCodeComponentsSelector = selectDesignerAbility(
  'canEditCodeComponents'
);
const selectIsPageLoading = (state: DesignerStores) =>
  state.UiStore.isPageLoading;

type CodeIslandElement = HTMLElement & {
  getState?: () => {status?: 'loading' | 'loaded' | 'error'};
};

type WindowWithCodeIslandStore = Window & {
  CodeIslandCustomElementStore?: CodeIslandCustomElementStore;
};

type ElementWithVisibilityCheck = Element & {
  checkVisibility?: (options: {
    checkOpacity: boolean;
    checkVisibilityCSS: boolean;
  }) => boolean;
};

function getCodeComponentId(element: CodeIslandElement): string | null {
  const loader = element.getAttribute('data-loader');
  if (!loader) return null;

  try {
    const parsed: unknown = JSON.parse(loader);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('tag' in parsed) ||
      parsed.tag !== 'SITE' ||
      !('val' in parsed) ||
      typeof parsed.val !== 'object' ||
      parsed.val === null ||
      !('componentId' in parsed.val) ||
      typeof parsed.val.componentId !== 'string'
    ) {
      return null;
    }

    return parsed.val.componentId;
  } catch {
    return null;
  }
}

function isElementVisible(element: Element): boolean {
  const elementWithVisibilityCheck = element as ElementWithVisibilityCheck;
  if (elementWithVisibilityCheck.checkVisibility) {
    try {
      return elementWithVisibilityCheck.checkVisibility({
        checkOpacity: true,
        checkVisibilityCSS: true,
      });
    } catch {
      // Fall back for browsers that reject the visibility options.
    }
  }

  let current: Element | null = element;
  while (current) {
    const style = current.ownerDocument.defaultView?.getComputedStyle(current);
    if (
      style?.display === 'none' ||
      style?.visibility === 'hidden' ||
      style?.visibility === 'collapse' ||
      Number.parseFloat(style?.opacity ?? '1') === 0
    ) {
      return false;
    }

    const root = current.getRootNode();
    current =
      current.parentElement ??
      (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE
        ? (root as ShadowRoot).host
        : null);
  }

  return true;
}

function getOnboardingCodeComponentId(
  editableComponentIds: ReadonlySet<string>
): string | null {
  try {
    const canvasDocument = getActiveDocument();
    if (!canvasDocument) return null;

    const firstEditableComponentId =
      editableComponentIds.values().next().value ?? null;
    let hasEditableComponentInstance = false;
    let firstMatch: {componentId: string; left: number; top: number} | null =
      null;

    for (const instance of canvasDocument.querySelectorAll<CodeIslandElement>(
      'code-island'
    )) {
      const componentId = getCodeComponentId(instance);
      if (!componentId || !editableComponentIds.has(componentId)) continue;

      hasEditableComponentInstance = true;
      if (instance.getState?.()?.status !== 'loaded') continue;

      const shadowRoot = instance.shadowRoot;
      const [contentRoot] = shadowRoot
        ? getCodeIslandContentElements(shadowRoot)
        : [];
      if (!contentRoot || !isElementVisible(contentRoot)) continue;

      const {height, left, top, width} = contentRoot.getBoundingClientRect();
      if (width <= 0 || height <= 0) continue;

      if (
        firstMatch === null ||
        top < firstMatch.top ||
        (top === firstMatch.top && left < firstMatch.left)
      ) {
        firstMatch = {componentId, left, top};
      }
    }

    // With no page instance, open the first existing AICC instead of waiting
    // for a CodeIsland update that will never arrive.
    return (
      firstMatch?.componentId ??
      (hasEditableComponentInstance ? null : firstEditableComponentId)
    );
  } catch {
    return null;
  }
}

export function getCodeIslandStore(): CodeIslandCustomElementStore | null {
  try {
    const activeWindow = getActiveDocument()
      ?.defaultView as WindowWithCodeIslandStore | null;

    return (
      activeWindow?.CodeIslandCustomElementStore ??
      CodeIslandCustomElementStore.getInstance() ??
      null
    );
  } catch {
    return CodeIslandCustomElementStore.getInstance() ?? null;
  }
}

function getCodeIslandCustomElementRegistry(): CustomElementRegistry | null {
  try {
    return getActiveDocument()?.defaultView?.customElements ?? null;
  } catch {
    return null;
  }
}

function useOnboardingCodeComponentId(
  editableComponentIds: ReadonlySet<string>,
  isEnabled: boolean
): string | null {
  const [componentId, setComponentId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isEnabled) {
      setComponentId(null);
      return;
    }

    let isCancelled = false;
    let unsubscribe = () => {};
    const updateCodeComponent = () => {
      if (isCancelled) return;

      setComponentId(getOnboardingCodeComponentId(editableComponentIds));
    };
    const subscribeToStore = () => {
      if (isCancelled) return false;

      const store = getCodeIslandStore();
      if (!store) return false;

      unsubscribe = store.subscribe(updateCodeComponent);
      updateCodeComponent();
      return true;
    };

    updateCodeComponent();
    if (!subscribeToStore()) {
      const customElementRegistry = getCodeIslandCustomElementRegistry();
      if (customElementRegistry) {
        customElementRegistry.whenDefined('code-island').then(() => {
          subscribeToStore();
        });
      }
    }

    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, [editableComponentIds, isEnabled]);

  return isEnabled ? componentId : null;
}

type VisualEditingOnboardingStep = 1 | 2 | 3;
type VisualEditingOnboardingSurface = 'components-button' | 'style-panel';

const VISUAL_EDITING_ONBOARDING_STEP_COUNT = 3;

// `videoHeight` is the design's crop for each clip, and it also keeps the
// popover from resizing while the clip loads its metadata. The clips are wider
// than their box, so they are cropped rather than letterboxed.
const ONBOARDING_STEPS: Record<
  VisualEditingOnboardingStep,
  {
    body: string;
    title: string;
    videoAlt: string;
    videoHeight: number;
    videoSrc: string;
  }
> = {
  1: {
    body: 'Select an element on the component canvas to get more precise editing controls.',
    title: 'New ways to visually edit an AI code component',
    videoAlt: 'Selecting an element on the component canvas',
    videoHeight: 144,
    videoSrc: VISUAL_EDITING_ONBOARDING_VIDEOS.elementSelection,
  },
  2: {
    body: 'Once you’ve made visual edits to the component, apply them with the AI Assistant chat.',
    title: 'Apply your edits via the AI Assistant',
    videoAlt: 'Applying visual edits from the AI Assistant chat',
    videoHeight: 148,
    videoSrc: VISUAL_EDITING_ONBOARDING_VIDEOS.applyViaAssistant,
  },
  3: {
    body: 'You’ll still be able to apply your edits even if you close the chat.',
    title: 'The bottom toolbar appears if you close the chat',
    videoAlt: 'Applying visual edits from the toolbar with the chat closed',
    videoHeight: 136,
    videoSrc: VISUAL_EDITING_ONBOARDING_VIDEOS.toolbarWithoutChat,
  },
};

const OnboardingVideo = emotionStyled('video')({
  display: 'block',
  height: '100%',
  objectFit: 'cover',
  width: '100%',
});

export const VisualEditingOnboardingPopover = ({
  anchorRef,
  hasArrow = true,
  onAdvance,
  onDismiss,
  placement,
  showTryItOut = false,
  step,
}: {
  anchorRef: AnchorRef;
  hasArrow?: boolean;
  onAdvance?: () => void;
  onDismiss: () => void;
  placement?: 'left' | 'left-start' | 'right' | 'right-start' | 'top-start';
  showTryItOut?: boolean;
  step: VisualEditingOnboardingStep;
}) => {
  const {body, title, videoAlt, videoHeight, videoSrc} = ONBOARDING_STEPS[step];
  const isLastStep = step === VISUAL_EDITING_ONBOARDING_STEP_COUNT;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <Popover
      anchorRef={anchorRef}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement={placement ?? 'left'}
      shouldHideOnClickOutside={false}
      width={VISUAL_EDITING_ONBOARDING_POPOVER_WIDTH}
    >
      <PopoverContent
        aria-label="Visual editing onboarding"
        colorVariant="blue"
        data-automation-id="visual-editing-onboarding-popover"
        data-step={step}
        hasArrow={hasArrow}
        isSeamless
      >
        <VStack
          align="stretch"
          // Advancing swaps the title, body and counter in place while focus
          // stays on the button, so without this only the button's new label
          // is announced.
          aria-live="polite"
          minWidth={0}
          padding="$3"
          position="relative"
          spacing={2}
          width={VISUAL_EDITING_ONBOARDING_POPOVER_WIDTH}
        >
          <Text font="heading">{title}</Text>
          <View position="absolute" right="$2" top="$1">
            <IconButton
              colorVariant="white"
              data-automation-id="visual-editing-onboarding-close"
              density="compact"
              Icon={CloseDefaultIcon}
              label="Close"
              onClick={onDismiss}
              variant="ghost"
            />
          </View>
          <View
            borderRadius="$1"
            height={videoHeight}
            minWidth={0}
            overflow="hidden"
          >
            <OnboardingVideo
              autoPlay={!prefersReducedMotion}
              // Without autoplay the clip needs a way to be watched, and the
              // first frame has to be there to show instead of an empty box.
              controls={prefersReducedMotion}
              data-automation-id="visual-editing-onboarding-video"
              // Remount on step change so the element loads the new source.
              key={videoSrc}
              loop
              muted
              playsInline
              preload="metadata"
              src={videoSrc}
              title={videoAlt}
            />
          </View>
          <Text>{body}</Text>
          <HStack align="baseline" justify="end" spacing={2}>
            <Text
              flex={1}
            >{`${step} of ${VISUAL_EDITING_ONBOARDING_STEP_COUNT}`}</Text>
            <Button
              colorVariant="white"
              data-automation-id={
                isLastStep
                  ? 'visual-editing-onboarding-done'
                  : showTryItOut
                    ? 'visual-editing-onboarding-try-it-out'
                    : 'visual-editing-onboarding-next'
              }
              onClick={isLastStep ? onDismiss : onAdvance}
              variant="outline"
            >
              {isLastStep ? 'Got it' : showTryItOut ? 'Try it out' : 'Next'}
            </Button>
          </HStack>
        </VStack>
      </PopoverContent>
    </Popover>
  );
};

// Single gate for VisualEditingOnboarding's early return; hosts also use it
// to skip anchor bookkeeping (e.g. DOM observers) when the popover can never
// show.
export function useCanShowVisualEditingOnboarding(): boolean {
  const isVisualEditingEnabled = useFeatureFlag(
    FEATURE_FLAGS.VIBE_COMPONENT_ELEMENT_SELECTION
  );
  const canEditCodeComponents = useDesignerSelector(
    canEditCodeComponentsSelector
  );
  const [isOnboardingFirstTimeUse] = useFirstTimeUse(
    VISUAL_EDITING_ONBOARDING_NOTE_ID
  );

  return (
    isVisualEditingEnabled && canEditCodeComponents && isOnboardingFirstTimeUse
  );
}

// Steps 2 and 3 talk about the chat, so they point at it rather than at the
// surface they are mounted in; with the chat closed they fall back to that
// surface instead of losing their anchor mid-tour.
export function resolveOnboardingAnchor({
  assistantChatAnchorRef,
  step,
  surfaceAnchorRef,
}: {
  assistantChatAnchorRef: HTMLElement | null;
  step: VisualEditingOnboardingStep;
  surfaceAnchorRef: AnchorRef;
}): AnchorRef {
  if (step === 1) return surfaceAnchorRef;

  return assistantChatAnchorRef ?? surfaceAnchorRef;
}

export const VisualEditingOnboarding = ({
  anchorRef,
  surface,
}: {
  anchorRef: AnchorRef;
  surface: VisualEditingOnboardingSurface;
}) => {
  const dispatch = useCodeComponentDispatch();
  const designerDispatch = useDispatch();
  const createCodeComponent = useCodeComponentCreated();
  const canShowOnboarding = useCanShowVisualEditingOnboarding();
  const isCodeComponentCanvas = useDesignerSelector(
    selectIsCodeComponentCanvas
  );
  const componentCanvasComponentId = useDesignerSelector(
    selectComponentCanvasComponentId
  );
  const isPageLoading = useDesignerSelector(selectIsPageLoading);
  const editableCodeComponentIds = useDesignerSelector(
    selectEditableCodeComponentIds
  );
  const hasCodeComponents = useDesignerSelector(selectHasCodeComponents);
  const showCreator = useCodeComponentSelector(selectShowCodeComponentCreator);
  const showOnboarding = useCodeComponentSelector(
    selectShowVisualEditingOnboarding
  );
  const onboardingStep = useCodeComponentSelector(
    selectVisualEditingOnboardingStep
  );
  const [pendingCodeComponentId, setPendingCodeComponentId] = React.useState<
    string | null
  >(null);
  const [, markOnboardingSeen] = useFirstTimeUse(
    VISUAL_EDITING_ONBOARDING_NOTE_ID
  );
  const [isLegacyOnboardingFirstTimeUse] = useFirstTimeUse(
    LEGACY_VISUAL_EDITING_ONBOARDING_NOTE_ID
  );
  const [isAiccUsageFirstTimeUse, markAiccUsed] =
    useFirstTimeUse(AICC_USED_NOTE_ID);

  React.useEffect(() => {
    if (
      isAiccUsageFirstTimeUse &&
      (editableCodeComponentIds.size > 0 || !isLegacyOnboardingFirstTimeUse)
    ) {
      markAiccUsed();
    }
  }, [
    editableCodeComponentIds,
    isAiccUsageFirstTimeUse,
    isLegacyOnboardingFirstTimeUse,
    markAiccUsed,
  ]);

  const hasUsedAicc =
    editableCodeComponentIds.size > 0 || !isAiccUsageFirstTimeUse;
  const isDiscoveryEligible =
    Boolean(anchorRef) &&
    canShowOnboarding &&
    surface === 'components-button' &&
    !isCodeComponentCanvas &&
    !isPageLoading &&
    !showOnboarding &&
    (hasCodeComponents || hasUsedAicc);
  const onboardingCodeComponentId = useOnboardingCodeComponentId(
    editableCodeComponentIds,
    isDiscoveryEligible
  );
  const assistantChatAnchorRef = useAssistantChatAnchor(
    canShowOnboarding && surface === 'style-panel' && showOnboarding
  );

  const handleAdvance = React.useCallback(() => {
    dispatch(visualEditingOnboardingAdvanced());
  }, [dispatch]);

  const handleDismiss = React.useCallback(() => {
    setPendingCodeComponentId(null);
    markOnboardingSeen();
    dispatch(visualEditingOnboardingDismissed());
  }, [dispatch, markOnboardingSeen]);

  React.useEffect(() => {
    if (
      pendingCodeComponentId === null ||
      isPageLoading ||
      !isCodeComponentCanvas ||
      componentCanvasComponentId !== pendingCodeComponentId
    ) {
      return;
    }

    setPendingCodeComponentId(null);
    dispatch(visualEditingOnboardingStarted());
  }, [
    componentCanvasComponentId,
    dispatch,
    isCodeComponentCanvas,
    isPageLoading,
    pendingCodeComponentId,
  ]);

  const handleTryItOut = React.useCallback(() => {
    if (onboardingCodeComponentId) {
      setPendingCodeComponentId(onboardingCodeComponentId);
      designerDispatch(
        switchToComponentCanvasStarted({
          componentId: onboardingCodeComponentId,
          entryKind: 'existing',
          analytics: {
            source: 'symbols panel',
            trigger: 'click',
            symbolId: onboardingCodeComponentId,
          },
        })
      );
      return;
    }

    createCodeComponent();
    dispatch(visualEditingOnboardingStarted());
  }, [
    createCodeComponent,
    designerDispatch,
    dispatch,
    onboardingCodeComponentId,
  ]);

  if (!anchorRef || !canShowOnboarding) {
    return null;
  }

  const shouldShowDiscovery =
    isDiscoveryEligible &&
    (editableCodeComponentIds.size === 0 || onboardingCodeComponentId !== null);
  const shouldShowCanvasTour =
    isCodeComponentCanvas &&
    showOnboarding &&
    surface === 'style-panel' &&
    !showCreator;

  if (!shouldShowDiscovery && !shouldShowCanvasTour) {
    return null;
  }

  if (shouldShowDiscovery) {
    return (
      <VisualEditingOnboardingPopover
        anchorRef={anchorRef}
        onAdvance={handleTryItOut}
        onDismiss={handleDismiss}
        placement="right"
        showTryItOut
        step={1}
      />
    );
  }

  return (
    <VisualEditingOnboardingPopover
      anchorRef={resolveOnboardingAnchor({
        assistantChatAnchorRef,
        step: onboardingStep,
        surfaceAnchorRef: anchorRef,
      })}
      // The first-generation step floats beside the Style panel without
      // pointing at anything specific (per the EXT-2068 design); the chat
      // steps keep their arrow.
      hasArrow={onboardingStep !== 1}
      onAdvance={handleAdvance}
      onDismiss={handleDismiss}
      placement={onboardingStep === 2 ? 'left' : 'left-start'}
      step={onboardingStep}
    />
  );
};
